import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  CreditCard,
  Zap,
  RefreshCw,
  ArrowRight,
  Info,
  Package,
  Check,
  Loader2,
  TrendingUp,
} from "lucide-react";
import {
  REMEDIATION_TIERS,
  BOOSTER_PACKS,
  RESCORE_FULL,
  PORTFOLIO_RESCORES,
  formatPrice,
  perUnitPrice,
  type Currency,
  type BoosterPack,
} from "@/components/subscriptions/remediationPricing";
import {
  getSubscriptionStatus,
  getCheckoutCurrency,
  getUsage,
  startTierCheckout,
  startBoosterCheckout,
  startRescoreCheckout,
  openBillingPortal,
  type SubscriptionStatus,
  type UsageStatus,
  type CheckoutResult,
  type CheckoutContext,
  formatBillingDate,
} from "@/components/subscriptions/subscriptionService";
import { useAuth } from "@/contexts/AuthContext";
import { useSiteContext } from "@/components/ruby-ai-v2/contexts/SiteContext";

// ── Brand gradient (violet → orange), matches Dashboard ──
const BRAND_GRADIENT = "linear-gradient(98deg,#8B5CF6 0%,#B16CEA 46%,#FF7A18 100%)";

const TIER_BLURB: Record<"starter" | "growth" | "scale", string> = {
  starter: "Managed remediation for a single website.",
  growth: "For small portfolios — manage up to three websites.",
  scale: "For agencies and multi-location brands — up to twelve websites, with a dedicated account manager.",
};

const Eyebrow = ({ children }: { children: React.ReactNode }) => (
  <div className="mb-2 inline-flex items-center gap-2.5 text-[11.5px] font-semibold uppercase tracking-[0.18em] text-og01-violet">
    <span className="h-[1.5px] w-6" style={{ background: BRAND_GRADIENT }} />
    {children}
  </div>
);

const Subscriptions = () => {
  const { user } = useAuth();
  const { activeSite, sites, switchSite, loading: sitesLoading } = useSiteContext();
  const [currency, setCurrency] = useState<Currency | null>(null);
  const [currencyLoading, setCurrencyLoading] = useState(true);
  const [status, setStatus] = useState<SubscriptionStatus | null>(null);
  const [usage, setUsage] = useState<UsageStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    getCheckoutCurrency().then((regionalCurrency) => {
      if (!active) return;
      // A manual choice made while the lookup was pending must win.
      setCurrency((current) => current ?? regionalCurrency);
      setCurrencyLoading(false);
    });
    return () => { active = false; };
  }, []);

  const checkoutContext = (): CheckoutContext => ({
    userId: user?.id,
    domain: activeSite?.domain || sites[0]?.domain,
    successUrl: `${window.location.origin}/subscriptions?checkout=success`,
    cancelUrl: `${window.location.origin}/subscriptions?checkout=cancel`,
  });

  useEffect(() => {
    const checkout = new URLSearchParams(window.location.search).get("checkout");
    if (checkout === "success") {
      toast.success("Payment received", {
        description: "Credits and invoices update when Stripe confirms the payment.",
      });
    } else if (checkout === "cancel") {
      toast.info("Checkout cancelled");
    }
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      const [s, u] = await Promise.all([getSubscriptionStatus(), getUsage()]);
      if (!active) return;
      setStatus(s);
      setUsage(u);
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, []);

  const handleCheckout = async (id: string, run: () => Promise<CheckoutResult>) => {
    setBusyId(id);
    try {
      const res = await run();
      if (res.ok) {
        window.location.href = res.url;
        return;
      }
      if ("reason" in res && res.reason === "not_configured") {
        toast.info("Checkout is being connected", {
          description: "The billing connection is not configured in this environment yet.",
        });
      } else if ("reason" in res && res.reason === "subscription_exists") {
        toast.info("A plan already exists for this site", {
          description: "Refresh your subscription details and use Manage billing to change the existing plan.",
        });
      } else if ("reason" in res && res.reason === "full_required") {
        toast.error("Full Intelligence Report required for this site", {
          description: "Check the selected domain. If you already purchased its report, contact support to reconcile the purchase.",
        });
      } else if ("reason" in res && res.reason === "no_active_tier") {
        toast.error("Active plan required", {
          description: "Boosters and rescores need an active Starter, Growth, or Scale plan first.",
        });
      } else if ("reason" in res && res.reason === "domain_required") {
        toast.error("Choose a site first", {
          description: "Checkout and the billing portal use a site on your account.",
        });
      } else if ("reason" in res && res.reason === "domain_not_owned") {
        toast.error("That site isn't on your account");
      } else {
        toast.error("Couldn't start checkout", { description: "Please try again shortly." });
      }
    } finally {
      setBusyId(null);
    }
  };

  const usagePct = useMemo(() => {
    if (!usage || usage.included <= 0) return 0;
    return Math.min(100, Math.round((usage.used / usage.included) * 100));
  }, [usage]);

  const activeTier = useMemo(
    () => REMEDIATION_TIERS.find((t) => t.id === (status?.tier || "").toLowerCase()) || null,
    [status]
  );

  return (
    <div className="animate-fade-in px-6 pb-14 pt-6 md:px-8">
      <div className="mx-auto w-full max-w-[1320px]">
        {/* ── Header ── */}
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <Eyebrow>Billing &amp; Usage</Eyebrow>
            <h1 className="font-heading text-[clamp(26px,3vw,38px)] font-normal leading-[1.05] text-og01-text">
              Subscriptions
            </h1>
            <p className="mt-1.5 text-[13.5px] text-og01-text-secondary">
              Manage your remediation plan, top up units, and re-run your audit.
            </p>
          </div>

          {/* Currency must be selected before any plan, booster or rescore checkout. */}
          <div className="flex flex-col items-start gap-2 sm:items-end">
            <span className="text-[13px] font-semibold text-og01-text">Select billing currency to see prices and continue</span>
            <div role="group" aria-label="Billing currency" className={`inline-flex items-center gap-1 rounded-[10px] border p-1 ${currency ? "border-white/[0.15] bg-og01-card" : "border-og01-violet bg-og01-card ring-2 ring-og01-violet/20"}`}>
            {(["AUD", "USD"] as Currency[]).map((c) => (
              <button
                key={c}
                aria-pressed={currency === c}
                onClick={() => setCurrency(c)}
                className="rounded-md px-3 py-1.5 text-[12.5px] font-semibold transition-colors"
                style={
                  currency === c
                    ? { background: "rgba(139,92,246,.18)", color: "#B79CFF" }
                    : { color: "var(--og01-text-muted, #8A8F9C)" }
                }
              >
                {c}
              </button>
            ))}
            </div>
          </div>
        </div>

        <p role="status" className="mb-6 text-[13px] text-og01-text-secondary">
          {currencyLoading && !currency
            ? "Detecting your region… Select AUD or USD above if prices do not appear."
            : "AUD is for Australia, New Zealand and supported Pacific markets; USD is for other markets. These are separate fixed prices, not currency conversions. Select a currency above to enable plan, booster and rescore checkout."}
        </p>

        {/* ── Row 1: Current plan + Units ── */}
        <div className="mb-10 grid grid-cols-1 gap-[18px] lg:grid-cols-2">
          {/* Current plan */}
          <section className="rounded-2xl border border-white/[0.07] bg-og01-card p-6">
            <div className="mb-5 flex items-center gap-2.5">
              <div className="grid h-[38px] w-[38px] flex-none place-items-center rounded-[11px] border border-white/[0.07] bg-[#0F0F18]">
                <CreditCard className="h-[18px] w-[18px] text-og01-violet" />
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-[0.14em] text-og01-text-muted">Current plan</div>
                <div className="text-[15px] font-semibold text-og01-text">
                  {loading ? "…" : activeTier ? activeTier.name : "No active plan"}
                </div>
              </div>
              {!loading && (
                <span
                  className="ml-auto rounded-full border px-2.5 py-[5px] text-[11px] font-semibold"
                  style={
                    status?.status === "active"
                      ? { color: "#22C55E", background: "rgba(34,197,94,.12)", borderColor: "rgba(34,197,94,.28)" }
                      : status?.status === "past_due"
                        ? { color: "#F59E0B", background: "rgba(245,158,11,.12)", borderColor: "rgba(245,158,11,.28)" }
                        : status?.status === "canceled"
                          ? { color: "#EF4444", background: "rgba(239,68,68,.12)", borderColor: "rgba(239,68,68,.28)" }
                          : { color: "#8A8F9C", background: "rgba(255,255,255,.05)", borderColor: "rgba(255,255,255,.1)" }
                  }
                >
                  {status?.status === "active"
                    ? status.cancellationDate ? "Active · Cancellation scheduled" : "Active"
                    : status?.status === "past_due"
                      ? "Past due"
                      : status?.status === "canceled"
                        ? "Canceled"
                        : "Not connected"}
                </span>
              )}
            </div>

            {loading ? (
              <div className="flex items-center gap-2 py-6 text-[13px] text-og01-text-secondary">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading your plan…
              </div>
            ) : activeTier ? (
              <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                <PlanStat label="Sites" value={`${activeTier.sites}`} />
                <PlanStat label="URLs / site" value={`${activeTier.urlsPerSite}`} />
                <PlanStat label="Units / month" value={`${activeTier.units}`} />
                <PlanStat label={status?.cancellationDate ? "Cancels" : "Renews"} value={formatBillingDate(status?.cancellationDate || status?.nextBillingDate)} />
              </div>
            ) : (
              <div className="rounded-xl border border-white/[0.07] bg-[#0F0F18] px-4 py-3.5">
                <p className="text-[13px] text-og01-text-secondary">
                  Your remediation plan will appear here once billing is connected. Plans are chosen when you subscribe.
                </p>
              </div>
            )}

            <button
              disabled={busyId === "portal"}
              onClick={() =>
                handleCheckout("portal", () =>
                  openBillingPortal({
                    domain: activeSite?.domain || sites[0]?.domain,
                    returnUrl: `${window.location.origin}/subscriptions`,
                  })
                )
              }
              className="mt-5 inline-flex items-center gap-2 rounded-[10px] border border-white/[0.07] bg-[#0F0F18] px-4 py-2 text-[13px] font-semibold text-og01-text transition-colors hover:border-white/[0.12] disabled:opacity-50"
            >
              {busyId === "portal" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
              {activeTier ? "Manage plan" : "Billing portal"}
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </section>

          {/* Units remaining */}
          <section className="rounded-2xl border border-white/[0.07] bg-og01-card p-6">
            <div className="mb-5 flex items-center gap-2.5">
              <div className="grid h-[38px] w-[38px] flex-none place-items-center rounded-[11px] border border-white/[0.07] bg-[#0F0F18]">
                <Zap className="h-[18px] w-[18px]" style={{ color: "#FF7A18" }} />
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-[0.14em] text-og01-text-muted">Remediation units</div>
                <div className="text-[15px] font-semibold text-og01-text">This billing cycle</div>
              </div>
            </div>

            {usage ? (
              <>
                <div className="mb-2 flex items-baseline gap-2">
                  <span className="font-heading text-[40px] leading-none text-og01-text">
                    {typeof usage.balance === "number"
                      ? Math.max(0, usage.balance)
                      : Math.max(0, usage.included - usage.used)}
                  </span>
                  <span className="text-[13px] text-og01-text-secondary">
                    {typeof usage.balance === "number"
                      ? "units available"
                      : `of ${usage.included} units left`}
                  </span>
                </div>
                <div className="mb-3 h-2 w-full overflow-hidden rounded-full bg-white/[0.07]">
                  <div className="h-full rounded-full" style={{ width: `${100 - usagePct}%`, background: BRAND_GRADIENT }} />
                </div>
                <div className="flex items-center justify-between text-[12.5px] text-og01-text-secondary">
                  <span>+{usage.boosterRemaining} booster units</span>
                  <span>Resets {formatBillingDate(usage.resetsOn)}</span>
                </div>
              </>
            ) : (
              <>
                <div className="mb-2 flex items-baseline gap-2">
                  <span className="font-heading text-[40px] leading-none text-og01-text-muted">—</span>
                  <span className="text-[13px] text-og01-text-secondary">units</span>
                </div>
                <div className="mb-3 h-2 w-full overflow-hidden rounded-full bg-white/[0.07]" />
                <div className="rounded-xl border border-white/[0.07] bg-[#0F0F18] px-4 py-3">
                  <p className="text-[12.5px] text-og01-text-secondary">
                    Usage tracking activates with your plan. One unit = one complete Ruby fix for a flagged issue.
                  </p>
                </div>
              </>
            )}
          </section>
        </div>

        {/* ── Plans (Starter / Growth / Scale) ── */}
        <div className="mb-[18px]">
          <Eyebrow>Plans</Eyebrow>
          <div className="flex flex-wrap items-end justify-between gap-3">
            <h2 className="font-heading text-[clamp(22px,2.6vw,30px)] font-normal leading-[1.1] text-og01-text">
              Managed Improvement plans
            </h2>
            <p className="max-w-[48ch] text-[13px] text-og01-text-secondary">
              Each plan includes a monthly remediation allowance and the number of websites you can
              manage. Change or upgrade anytime from the billing portal.
            </p>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <span className="text-[13px] font-semibold text-og01-text">Plan billing currency:</span>
            <div role="group" aria-label="Plan billing currency:" className="inline-flex gap-1 rounded-[10px] border border-og01-violet/50 bg-og01-card p-1">
              {(["AUD", "USD"] as Currency[]).map((c) => (
                <button key={c} type="button" aria-pressed={currency === c} onClick={() => setCurrency(c)}
                  className={`rounded-md px-4 py-2 text-[13px] font-semibold transition-colors ${currency === c ? "bg-og01-violet text-white" : "text-og01-text-secondary hover:bg-white/10"}`}>
                  {c}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="mb-6 grid grid-cols-1 gap-[18px] md:grid-cols-3">
          {REMEDIATION_TIERS.map((tier) => {
            const hasPlan = !!activeTier && (status?.status === "active" || status?.status === "past_due");
            const isCurrent = hasPlan && activeTier?.id === tier.id;
            const btnId = `tier-${tier.id}`;
            return (
              <article
                key={tier.id}
                className="relative flex flex-col rounded-2xl border bg-og01-card p-6 transition-all duration-200 hover:-translate-y-[3px]"
                style={{
                  borderColor: isCurrent
                    ? "color-mix(in srgb, #22C55E 40%, transparent)"
                    : tier.highlight
                      ? "color-mix(in srgb, #8B5CF6 45%, transparent)"
                      : "rgba(255,255,255,0.07)",
                }}
              >
                {tier.highlight && !isCurrent && (
                  <span
                    className="absolute right-4 top-4 rounded-full px-2.5 py-1 text-[10.5px] font-semibold"
                    style={{ color: "#B79CFF", background: "rgba(139,92,246,.14)" }}
                  >
                    Most popular
                  </span>
                )}
                {isCurrent && (
                  <span
                    className="absolute right-4 top-4 rounded-full px-2.5 py-1 text-[10.5px] font-semibold"
                    style={{ color: "#22C55E", background: "rgba(34,197,94,.14)" }}
                  >
                    Current
                  </span>
                )}

                <div className="font-heading text-[22px] text-og01-text">{tier.name}</div>
                <p className="mt-1 min-h-[52px] text-[12.5px] text-og01-text-secondary">{TIER_BLURB[tier.id]}</p>

                <div className="mt-3 flex items-baseline gap-1.5">
                  <span className="font-heading text-[34px] leading-none text-og01-text">{currency ? formatPrice(tier.monthly, currency) : "—"}</span>
                  <span className="text-[12.5px] text-og01-text-secondary">/ month</span>
                </div>
                <div className="mt-0.5 text-[11.5px] text-og01-text-muted">+ {currency ? formatPrice(tier.setup, currency) : "—"} one-off setup</div>

                <ul className="mb-5 mt-4 space-y-2 text-[13px] text-og01-text-secondary">
                  <li className="flex items-center gap-2">
                    <Check className="h-3.5 w-3.5 flex-none" style={{ color: "#22C55E" }} />
                    {tier.sites} managed {tier.sites === 1 ? "website" : "websites"}
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-3.5 w-3.5 flex-none" style={{ color: "#22C55E" }} />
                    {tier.units} remediation units / month
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-3.5 w-3.5 flex-none" style={{ color: "#22C55E" }} />
                    Up to {tier.urlsPerSite} URLs / site
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-3.5 w-3.5 flex-none" style={{ color: "#22C55E" }} />
                    {tier.sla}
                  </li>
                </ul>

                <div className="mt-auto">
                  {isCurrent ? (
                    <button
                      disabled
                      className="w-full rounded-[10px] border border-white/[0.07] bg-[#0F0F18] px-4 py-2.5 text-[13.5px] font-semibold text-og01-text-muted"
                    >
                      Current plan
                    </button>
                  ) : hasPlan ? (
                    <button
                      disabled={busyId === btnId}
                      onClick={() =>
                        handleCheckout(btnId, () =>
                          openBillingPortal({
                            domain: activeSite?.domain || sites[0]?.domain,
                            returnUrl: `${window.location.origin}/subscriptions`,
                          })
                        )
                      }
                      className="inline-flex w-full items-center justify-center gap-2 rounded-[10px] border border-white/[0.07] bg-[#0F0F18] px-4 py-2.5 text-[13.5px] font-semibold text-og01-text transition-colors hover:border-white/[0.12] disabled:opacity-50"
                    >
                      {busyId === btnId ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                      Change plan
                    </button>
                  ) : (
                    <button
                      disabled={!currency || busyId === btnId}
                      onClick={() => { if (currency) handleCheckout(btnId, () => startTierCheckout(tier, currency, checkoutContext())); }}
                      className="inline-flex w-full items-center justify-center gap-2 rounded-[10px] border-none px-4 py-2.5 text-[13.5px] font-semibold text-white transition-all hover:brightness-110 disabled:opacity-50"
                      style={{ background: BRAND_GRADIENT, boxShadow: "0 8px 26px -12px rgba(255,122,24,.65)" }}
                    >
                      {busyId === btnId ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                      Subscribe
                    </button>
                  )}
                </div>
              </article>
            );
          })}
        </div>

        <div className="mb-10 flex items-start gap-3 rounded-xl border border-white/[0.07] bg-[#0F0F18] px-4 py-3">
          <Info className="mt-0.5 h-4 w-4 flex-none text-og01-violet" />
          <p className="text-[12.5px] text-og01-text-secondary">
            Changing between Starter, Growth and Scale is handled in the Stripe billing portal, which
            prorates the difference — you won't be charged twice or create a second subscription.
          </p>
        </div>

        {/* ── Boosters ── */}
        <div className="mb-[18px]">
          <Eyebrow>Top up</Eyebrow>
          <div className="flex flex-wrap items-end justify-between gap-3">
            <h2 className="font-heading text-[clamp(22px,2.6vw,30px)] font-normal leading-[1.1] text-og01-text">
              Buy a booster
            </h2>
            <p className="max-w-[48ch] text-[13px] text-og01-text-secondary">
              Out of units before your cycle resets? Top up instantly. Booster units behave exactly like monthly units and
              <b className="text-og01-text-secondary"> persist until used.</b>
            </p>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <label htmlFor="booster-site" className="text-[13px] font-semibold text-og01-text">Website to boost:</label>
            <select id="booster-site" aria-label="Website for booster checkout"
              value={activeSite?.domain || sites[0]?.domain || ""}
              disabled={sitesLoading || sites.length === 0}
              onChange={(e) => { const site = sites.find((s) => s.domain === e.target.value); if (site) void switchSite(site); }}
              className="min-w-[220px] rounded-lg border border-og01-violet/50 bg-og01-card px-3 py-2 text-[13px] text-og01-text">
              {sites.length === 0 && <option value="">No websites available</option>}
              {sites.map((site) => <option key={site.id} value={site.domain}>{site.displayName || site.domain} ({site.domain})</option>)}
            </select>
            {sites.length === 0 && !sitesLoading && <span role="status" className="text-[12px] text-og01-text-secondary">No website is linked to your account. Add or import a site before buying a booster.</span>}
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <span className="text-[13px] font-semibold text-og01-text">Booster billing currency:</span>
            <div role="group" aria-label="Booster billing currency" className="inline-flex gap-1 rounded-[10px] border border-og01-violet/50 bg-og01-card p-1">
              {(["AUD", "USD"] as Currency[]).map((c) => (
                <button key={c} type="button" aria-pressed={currency === c}
                  onClick={() => setCurrency(c)}
                  className={`rounded-md px-4 py-2 text-[13px] font-semibold transition-colors ${currency === c ? "bg-og01-violet text-white" : "text-og01-text-secondary hover:bg-white/10"}`}>
                  {c}
                </button>
              ))}
            </div>
            <span className="text-[12px] text-og01-text-secondary">AUD: AU, NZ &amp; supported Pacific markets · USD: other markets</span>
          </div>
        </div>

        <div className="mb-10 grid grid-cols-1 gap-[18px] sm:grid-cols-2 lg:grid-cols-4">
          {BOOSTER_PACKS.map((pack: BoosterPack) => (
            <article
              key={pack.id}
              className="relative flex flex-col rounded-2xl border border-white/[0.07] bg-og01-card p-6 transition-all duration-200 hover:-translate-y-[3px] hover:border-white/[0.12]"
            >
              {pack.badge && (
                <span
                  className="absolute right-4 top-4 rounded-full px-2.5 py-1 text-[10.5px] font-semibold"
                  style={{ color: "#FF7A18", background: "rgba(255,122,24,.12)" }}
                >
                  {pack.badge}
                </span>
              )}
              <div className="mb-4 grid h-[42px] w-[42px] place-items-center rounded-[11px] border border-white/[0.07] bg-[#0F0F18]">
                <Package className="h-5 w-5 text-og01-violet" />
              </div>
              <div className="flex items-baseline gap-1.5">
                <span className="font-heading text-[40px] leading-none text-og01-text">{pack.units}</span>
                <span className="text-[13px] text-og01-text-secondary">units</span>
              </div>
              <div className="mt-3 text-[20px] font-semibold text-og01-text">{currency ? formatPrice(pack.price, currency) : "—"}</div>
              <div className="mt-0.5 text-[12px] text-og01-text-muted">{currency ? perUnitPrice(pack, currency) : "Select AUD or USD below to enable checkout"}</div>
              <div role="group" aria-label={`Currency for ${pack.units}-unit booster`} className="mt-3 inline-flex w-fit gap-1 rounded-lg border border-og01-violet/50 bg-[#0F0F18] p-1">
                {(["AUD", "USD"] as Currency[]).map((c) => (
                  <button key={c} type="button" aria-pressed={currency === c} onClick={() => setCurrency(c)}
                    className={`rounded-md px-3 py-1.5 text-[12px] font-semibold transition-colors ${currency === c ? "bg-og01-violet text-white" : "text-og01-text-secondary hover:bg-white/10"}`}>
                    {c}
                  </button>
                ))}
              </div>

              <button
                disabled={!currency || busyId === pack.id}
                onClick={() => { if (currency) handleCheckout(pack.id, () => startBoosterCheckout(pack, currency, checkoutContext())); }}
                className="mt-5 inline-flex items-center justify-center gap-2 rounded-[10px] border-none px-4 py-2.5 text-[13.5px] font-semibold text-white transition-all hover:brightness-110 disabled:opacity-50"
                style={{ background: BRAND_GRADIENT, boxShadow: "0 8px 26px -12px rgba(255,122,24,.65)" }}
              >
                {busyId === pack.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
                Buy booster
              </button>
            </article>
          ))}
        </div>

        {/* ── Rescore ── */}
        <div className="mb-[18px]">
          <Eyebrow>Refresh</Eyebrow>
          <h2 className="font-heading text-[clamp(22px,2.6vw,30px)] font-normal leading-[1.1] text-og01-text">
            Re-run your audit
          </h2>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <span className="text-[13px] font-semibold text-og01-text">Rescore billing currency:</span>
            <div role="group" aria-label="Rescore billing currency:" className="inline-flex gap-1 rounded-[10px] border border-og01-violet/50 bg-og01-card p-1">
              {(["AUD", "USD"] as Currency[]).map((c) => (
                <button key={c} type="button" aria-pressed={currency === c} onClick={() => setCurrency(c)}
                  className={`rounded-md px-4 py-2 text-[13px] font-semibold transition-colors ${currency === c ? "bg-og01-violet text-white" : "text-og01-text-secondary hover:bg-white/10"}`}>
                  {c}
                </button>
              ))}
            </div>
          </div>
        </div>

        <section className="mb-10 grid items-center gap-6 overflow-hidden rounded-2xl border border-white/[0.07] bg-og01-card p-7 md:grid-cols-[1fr_auto]">
          <div className="flex items-start gap-4">
            <div className="grid h-[46px] w-[46px] flex-none place-items-center rounded-[12px] border border-white/[0.07] bg-[#0F0F18]">
              <RefreshCw className="h-[22px] w-[22px]" style={{ color: "#22C55E" }} />
            </div>
            <div>
              <h3 className="text-base font-semibold text-og01-text">{RESCORE_FULL.name}</h3>
              <p className="mt-1 max-w-[62ch] text-[13.5px] text-og01-text-secondary">{RESCORE_FULL.description}</p>
              <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[12.5px] text-og01-text-secondary">
                <span className="inline-flex items-center gap-1.5">
                  <TrendingUp className="h-3.5 w-3.5" style={{ color: "#22C55E" }} /> Track improvement over time
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Check className="h-3.5 w-3.5" style={{ color: "#22C55E" }} /> Full diagnostic + remediation report
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-5 md:flex-col md:items-end">
            <div className="font-heading text-[34px] leading-none text-og01-text">{currency ? formatPrice(RESCORE_FULL.price, currency) : "—"}</div>
            <button
              disabled={!currency || busyId === RESCORE_FULL.id}
              onClick={() => { if (currency) handleCheckout(RESCORE_FULL.id, () => startRescoreCheckout(RESCORE_FULL, currency, checkoutContext())); }}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[10px] border-none px-5 py-2.5 text-[13.5px] font-semibold text-white transition-all hover:brightness-110 disabled:opacity-50"
              style={{ background: BRAND_GRADIENT, boxShadow: "0 8px 26px -12px rgba(255,122,24,.65)" }}
            >
              {busyId === RESCORE_FULL.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Re-run audit
            </button>
          </div>
        </section>

        {/* Portfolio prices approved; checkout remains gated until secure multi-site fulfillment exists. */}
        <div className="mb-10 grid grid-cols-1 gap-[18px] md:grid-cols-2">
          {PORTFOLIO_RESCORES.map((bundle) => (
            <article key={bundle.id} className="rounded-2xl border border-white/[0.07] bg-og01-card p-6">
              <div className="flex items-center justify-between gap-3">
                <h3 className="font-heading text-[23px] text-og01-text">{bundle.label}</h3>
                <span className="rounded-full bg-white/[0.07] px-2 py-1 text-[11px] text-og01-text-secondary">Coming soon</span>
              </div>
              <p className="mt-2 text-[13px] text-og01-text-secondary">
                One complete Full Rescore report per website, for {bundle.sites} eligible managed websites.
              </p>
              <p className="mt-3 font-heading text-[32px] text-og01-text">
                {currency ? formatPrice(bundle.price, currency) : "Choose AUD or USD above"}
              </p>
              <p className="mt-2 text-[12px] text-og01-text-secondary">
                Portfolio checkout opens after secure site selection and individual report delivery are verified.
              </p>
              <button type="button" disabled className="mt-4 w-full rounded-[10px] border border-white/[0.07] bg-[#0F0F18] px-4 py-2.5 text-[13px] text-og01-text-muted">
                Portfolio rescore — not yet available
              </button>
            </article>
          ))}
        </div>

        {/* ── Footnote ── */}
        <div className="flex items-start gap-3 rounded-xl border border-white/[0.07] bg-[#0F0F18] px-4 py-3">
          <Info className="mt-0.5 h-4 w-4 flex-none text-og01-violet" />
          <p className="text-[12.5px] text-og01-text-secondary">
            Boosters and Rescore are billed as one-off purchases. Australia, New Zealand and Pacific markets use fixed
            AUD prices; other countries use fixed USD prices. Stripe may offer payment in your local currency at checkout.
            Buy and Manage open Stripe for the signed-in account.
          </p>
        </div>
      </div>
    </div>
  );
};

const PlanStat = ({ label, value }: { label: string; value: string }) => (
  <div className="flex items-center justify-between rounded-[10px] border border-white/[0.07] bg-[#0F0F18] px-3.5 py-[11px]">
    <span className="text-[12.5px] text-og01-text-secondary">{label}</span>
    <span className="text-[13px] font-semibold text-og01-text">{value}</span>
  </div>
);

export default Subscriptions;

